import { useEffect, useState, type FormEvent } from 'react';
import { format, parseISO } from 'date-fns';
import { useAppStore } from '../../stores/appStore';
import { calendarApi, type CalendarEvent, type DayNote } from '../../lib/api';

/**
 * Day detail panel: all events for the selected date + day notes.
 * Opens as a modal/side panel from calendar date click.
 */
export function DayPanel() {
  const date = useAppStore((s) => s.selectedDate);
  const closeDay = useAppStore((s) => s.closeDay);
  const openEventEditor = useAppStore((s) => s.openEventEditor);
  const calendars = useAppStore((s) => s.calendars);
  const user = useAppStore((s) => s.user);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [notes, setNotes] = useState<DayNote[]>([]);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    calendarApi
      .day(date)
      .then((res) => {
        setEvents(res.events);
        setNotes(res.notes);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [date]);

  if (!date) return null;

  const calColor = (id: string) =>
    calendars.find((c) => c.id === id)?.color || '#6366F1';
  const calName = (id: string) =>
    calendars.find((c) => c.id === id)?.name || 'Calendar';

  async function addNote(e: FormEvent) {
    e.preventDefault();
    if (!noteText.trim() || !date || !user) return;
    try {
      const { note } = await calendarApi.createDayNote(date, noteText.trim());
      setNotes((n) => [...n, note]);
      setNoteText('');
    } catch (err) {
      console.error(err);
    }
  }

  const label = (() => {
    try {
      return format(parseISO(date), 'EEEE, MMMM d, yyyy');
    } catch {
      return date;
    }
  })();

  return (
    <div
      className="modal-backdrop fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeDay();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl border border-slate-700 bg-slate-900 shadow-2xl sm:rounded-3xl light:border-slate-200 light:bg-white">
        <div className="flex items-start justify-between border-b border-slate-800 px-5 py-4 light:border-slate-200">
          <div>
            <h2 className="text-xl font-bold">{label}</h2>
            <p className="text-sm text-slate-400">
              {events.length} event{events.length === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={closeDay}
            className="rounded-xl bg-slate-800 px-3 py-2 text-sm font-semibold hover:bg-slate-700 light:bg-slate-100"
          >
            Close
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-slate-400">Loading…</p>
          ) : events.length === 0 ? (
            <p className="rounded-xl bg-slate-800/50 px-4 py-6 text-center text-slate-400 light:bg-slate-100">
              No events this day
            </p>
          ) : (
            <ul className="space-y-2">
              {events.map((ev) => (
                <li key={ev.id}>
                  <button
                    type="button"
                    onClick={() => {
                      // Master id for recurring occurrence edits
                      const masterId = ev.id.includes('__')
                        ? ev.id.split('__')[0]
                        : ev.id;
                      openEventEditor({ ...ev, id: masterId });
                    }}
                    className="flex w-full items-start gap-3 rounded-xl border border-slate-700/60 bg-slate-800/40 p-3 text-left transition hover:border-violet-500/50 light:border-slate-200 light:bg-slate-50"
                  >
                    <span
                      className="mt-1 h-3 w-3 shrink-0 rounded-full"
                      style={{ background: calColor(ev.calendar_id) }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{ev.title}</div>
                      <div className="text-sm text-slate-400">
                        {ev.all_day
                          ? 'All day'
                          : `${format(parseISO(ev.start_at), 'h:mm a')} – ${format(parseISO(ev.end_at), 'h:mm a')}`}
                        {' · '}
                        {calName(ev.calendar_id)}
                      </div>
                      {ev.location && (
                        <div className="mt-0.5 text-xs text-slate-500">
                          📍 {ev.location}
                        </div>
                      )}
                      {ev.description && (
                        <p className="mt-1 text-sm text-slate-300">
                          {ev.description}
                        </p>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div>
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-400">
              Day notes
            </h3>
            {notes.length === 0 && (
              <p className="mb-2 text-sm text-slate-500">No notes yet</p>
            )}
            <ul className="mb-3 space-y-2">
              {notes.map((n) => (
                <li
                  key={n.id}
                  className="flex items-start gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-100 light:bg-amber-50 light:text-amber-900"
                >
                  <span className="min-w-0 flex-1">{n.content}</span>
                  {user && (
                    <button
                      type="button"
                      className="shrink-0 rounded px-2 py-0.5 text-xs font-bold text-red-300 hover:bg-red-500/20"
                      title="Delete note"
                      onClick={async () => {
                        if (!confirm('Delete this note?')) return;
                        try {
                          await calendarApi.deleteDayNote(n.id);
                          setNotes((list) => list.filter((x) => x.id !== n.id));
                        } catch (err) {
                          console.error(err);
                          alert(
                            err instanceof Error
                              ? err.message
                              : 'Could not delete note'
                          );
                        }
                      }}
                    >
                      Delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {user && (
              <form onSubmit={addNote} className="flex gap-2">
                <input
                  className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 text-sm outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
                  placeholder="Add a note for this day…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <button
                  type="submit"
                  className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white"
                >
                  Add
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-slate-800 p-4 light:border-slate-200">
          <button
            type="button"
            onClick={() => openEventEditor(null, date)}
            className="w-full rounded-xl bg-violet-600 py-3 text-base font-bold text-white hover:bg-violet-500"
          >
            + Add event on this day
          </button>
        </div>
      </div>
    </div>
  );
}
